/**
 * Client-side image optimization: downscale to a max dimension and re-encode
 * as WebP before uploading.
 *
 * - Never upscales. The preset is the max length of the LONGEST side.
 * - Animated / vector / icon formats (GIF, SVG, ICO) pass through untouched so
 *   animation and vector fidelity are preserved.
 * - If the browser cannot encode WebP, or the result is not smaller than the
 *   original, the original file is returned unchanged.
 */

export const IMAGE_SIZE_PRESETS = {
  small: { label: "Small", maxDimension: 600 },
  medium: { label: "Medium", maxDimension: 1200 },
  large: { label: "Large", maxDimension: 2000 },
  original: { label: "Original", maxDimension: null },
};

export const DEFAULT_IMAGE_SIZE = "large";

export const IMAGE_SIZE_OPTIONS = Object.keys(IMAGE_SIZE_PRESETS).map(
  (value) => ({
    value,
    label: IMAGE_SIZE_PRESETS[value].maxDimension
      ? `${IMAGE_SIZE_PRESETS[value].label} (${IMAGE_SIZE_PRESETS[value].maxDimension}px)`
      : IMAGE_SIZE_PRESETS[value].label,
  })
);

// Order used to pick the "largest" preset when an image is used in many places.
const PRESET_RANK = { small: 1, medium: 2, large: 3, original: 4 };

export const largerImageSize = (a, b) =>
  (PRESET_RANK[a] || 0) >= (PRESET_RANK[b] || 0) ? a : b;

const PASSTHROUGH_TYPES = [
  "image/gif",
  "image/svg+xml",
  "image/x-icon",
  "image/vnd.microsoft.icon",
];
const PASSTHROUGH_EXT_RE = /\.(gif|svg|ico)$/i;
const RASTER_EXT_RE = /\.(png|jpe?g|webp|bmp|avif)$/i;

/** True when the file is a raster image we can safely re-encode. */
export const isOptimizableImage = (file) => {
  if (!file) return false;
  const type = (file.type || "").toLowerCase();
  const name = file.name || "";
  if (PASSTHROUGH_TYPES.includes(type) || PASSTHROUGH_EXT_RE.test(name)) {
    return false;
  }
  return type.startsWith("image/") || RASTER_EXT_RE.test(name);
};

export const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let val = bytes;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i += 1;
  }
  return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const loadBitmap = async (file) => {
  // createImageBitmap honours EXIF orientation with this option.
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch (e) {
      // fall through to <img> decoding
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Unable to decode image"));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const replaceExtension = (name, ext) => {
  const base = (name || "image").replace(/\.[^./\\]+$/, "");
  return `${base}.${ext}`;
};

/**
 * Optimize an image file.
 * @param {File|Blob} file
 * @param {{ maxDimension?: number|null, quality?: number, minBytes?: number }} opts
 * @returns {Promise<{file: File|Blob, optimized: boolean, originalSize: number,
 *   newSize: number, width?: number, height?: number, reason?: string}>}
 */
export const optimizeImageFile = async (
  file,
  { maxDimension = null, quality = 0.82, minBytes = 0 } = {}
) => {
  const originalSize = file?.size || 0;
  const unchanged = (reason, extra = {}) => ({
    file,
    optimized: false,
    originalSize,
    newSize: originalSize,
    reason,
    ...extra,
  });

  if (!isOptimizableImage(file)) return unchanged("unsupported");
  if (!maxDimension) return unchanged("original");
  if (originalSize < minBytes) return unchanged("small-file");

  let source;
  try {
    source = await loadBitmap(file);
  } catch (e) {
    return unchanged("decode-failed");
  }

  const srcW = source.width;
  const srcH = source.height;
  const scale = Math.min(1, maxDimension / Math.max(srcW, srcH));
  const width = Math.max(1, Math.round(srcW * scale));
  const height = Math.max(1, Math.round(srcH * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, width, height);
  if (typeof source.close === "function") source.close();

  const blob = await canvasToBlob(canvas, "image/webp", quality);
  // Some browsers silently fall back to PNG when WebP is unsupported.
  if (!blob || blob.type !== "image/webp") {
    return unchanged("webp-unsupported", { width: srcW, height: srcH });
  }
  if (blob.size >= originalSize) {
    return unchanged("not-smaller", { width: srcW, height: srcH });
  }

  const name = replaceExtension(file.name, "webp");
  const out =
    typeof File === "function"
      ? new File([blob], name, { type: "image/webp", lastModified: Date.now() })
      : Object.assign(blob, { name });

  return {
    file: out,
    optimized: true,
    originalSize,
    newSize: out.size,
    width,
    height,
  };
};

/** Human-readable summary for notifications. */
export const describeOptimization = (result) => {
  if (!result) return "";
  if (!result.optimized) {
    return `Uploaded original (${formatBytes(result.originalSize)})`;
  }
  return `Optimized ${formatBytes(result.originalSize)} → ${formatBytes(
    result.newSize
  )} (${result.width}×${result.height})`;
};
